/**
 * Rotating route (shotgun): play order starts at teams.starting_hole_id and wraps 1–12.
 * "Next hole" = first incomplete hole in that order. Hole ids normalized to integers for DB/string quirks.
 */

import { HOLES } from './courseConfig'

const N = 12

/** @param {unknown} id */
export function normalizeHoleId(id) {
  const n = Number(id)
  if (!Number.isFinite(n) || n < 1 || n > N) return null
  return Math.floor(n)
}

/**
 * @param {Iterable<unknown>} completedIds
 * @returns {Set<number>}
 */
export function normalizeCompletedSet(completedIds) {
  const set = new Set()
  for (const id of completedIds) {
    const n = normalizeHoleId(id)
    if (n !== null) set.add(n)
  }
  return set
}

/**
 * @param {number} startHoleId 1–12
 * @returns {number[]} length 12, unique hole ids in play order
 */
export function buildRotatingRouteOrder(startHoleId) {
  const start = normalizeHoleId(startHoleId) ?? 1
  const order = []
  for (let i = 0; i < N; i++) {
    const id = ((start - 1 + i) % N) + 1
    order.push(id)
  }
  return order
}

/**
 * @param {number[]} playOrder from buildRotatingRouteOrder
 * @param {Iterable<unknown>} completedIds
 * @returns {number | null} next hole id or null if all done
 */
export function getNextIncomplete(playOrder, completedIds) {
  const done = normalizeCompletedSet(completedIds)
  for (const hid of playOrder) {
    if (!done.has(hid)) return hid
  }
  return null
}

/**
 * @param {number | null | undefined} startingHoleId from team row; null/undefined → linear from hole 1
 * @param {Iterable<unknown>} completedHoleIds
 * @returns {number | null}
 */
export function getNextHoleIdForTeam(startingHoleId, completedHoleIds) {
  const start = normalizeHoleId(startingHoleId) ?? 1
  const playOrder = buildRotatingRouteOrder(start)
  return getNextIncomplete(playOrder, completedHoleIds)
}

/** @param {number} holeId */
export function getHoleConfigById(holeId) {
  const n = normalizeHoleId(holeId)
  if (n === null) return undefined
  return HOLES.find((h) => h.id === n)
}
